import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable wrapper defined at module scope so children don't re-mount on parent re-render
const BgWrapper = ({ logoUrl, children }) => (
    <div className="min-h-screen flex relative">
        <div className="absolute inset-0 z-0">
            {logoUrl && (
                <div className="absolute inset-0 bg-center bg-no-repeat" style={{ backgroundImage: `url(${logoUrl})`, backgroundSize: 'contain', opacity: 0.18 }} />
            )}
            <div className="absolute inset-0 bg-[#FAF9F6]/90" />
        </div>
        <div className="relative z-10 flex-1 flex items-center justify-center p-4">
            {children}
        </div>
    </div>
);

const ForgotPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const { forgotPassword, resetPassword } = useAuth();

    // Forgot password state
    const [email, setEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);

    // Reset password state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [logoUrl, setLogoUrl] = useState(null);

    useEffect(() => {
        axios.get(`${API_URL}/api/settings`).then(res => {
            if (res.data?.logo_url) setLogoUrl(res.data.logo_url);
        }).catch(() => {});
    }, []);

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await forgotPassword(email);
        
        if (result.success) {
            setEmailSent(true);
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        const result = await resetPassword(token, newPassword);
        
        if (result.success) {
            setResetSuccess(true);
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    // Reset password form (when token is present)
    if (token) {
        if (resetSuccess) {
            return (
                <BgWrapper logoUrl={logoUrl}>
                    <div className="w-full max-w-md">
                        <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8 text-center">
                            <CheckCircle className="h-16 w-16 text-[#10B981] mx-auto mb-4" />
                            <h2 className="text-2xl font-semibold text-[#1F2937] mb-2 font-['Outfit']">
                                Password Reset Successful
                            </h2>
                            <p className="text-[#4B5563] mb-6">
                                Your password has been reset successfully. You can now sign in with your new password.
                            </p>
                            <Button
                                onClick={() => navigate('/login')}
                                className="bg-[#064E3B] hover:bg-[#047857] text-white"
                                data-testid="go-to-login-button"
                            >
                                Go to Login
                            </Button>
                        </div>
                    </div>
                </BgWrapper>
            );
        }

        return (
            <BgWrapper logoUrl={logoUrl}>
                <div className="w-full max-w-md">
                    {logoUrl && <div className="flex justify-center mb-6"><img src={logoUrl} alt="School Logo" className="w-20 h-20 rounded-xl object-contain" /></div>}
                    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8">
                        <h2 className="text-2xl font-semibold text-[#1F2937] mb-2 font-['Outfit']">
                            Reset Your Password
                        </h2>
                        <p className="text-[#4B5563] mb-6">
                            Enter your new password below
                        </p>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleResetPassword} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="newPassword">New Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4B5563]" />
                                    <Input
                                        id="newPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter new password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="pl-10 pr-10 input-field"
                                        required
                                        data-testid="new-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] hover:text-[#1F2937]"
                                    >
                                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4B5563]" />
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        placeholder="Confirm new password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="pl-10 input-field"
                                        required
                                        data-testid="confirm-password-input"
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[#064E3B] hover:bg-[#047857] text-white h-11"
                                data-testid="reset-password-button"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Resetting...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </Button>
                        </form>
                    </div>
                </div>
            </BgWrapper>
        );
    }

    // Forgot password form (request reset email)
    if (emailSent) {
        return (
            <BgWrapper logoUrl={logoUrl}>
                <div className="w-full max-w-md">
                    <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8 text-center">
                        <Mail className="h-16 w-16 text-[#064E3B] mx-auto mb-4" />
                        <h2 className="text-2xl font-semibold text-[#1F2937] mb-2 font-['Outfit']">
                            Check Your Email
                        </h2>
                        <p className="text-[#4B5563] mb-6">
                            If an account exists for <strong>{email}</strong>, we've sent password reset instructions to that email address.
                        </p>
                        <Link 
                            to="/login"
                            className="text-[#064E3B] hover:text-[#047857] font-medium"
                        >
                            Back to Login
                        </Link>
                    </div>
                </div>
            </BgWrapper>
        );
    }

    return (
        <BgWrapper logoUrl={logoUrl}>
            <div className="w-full max-w-md">
                {logoUrl && <div className="flex justify-center mb-6"><img src={logoUrl} alt="School Logo" className="w-20 h-20 rounded-xl object-contain" /></div>}
                <div className="bg-white rounded-xl border border-black/5 shadow-sm p-8">
                    <Link 
                        to="/login" 
                        className="inline-flex items-center text-[#4B5563] hover:text-[#1F2937] mb-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Back to Login
                    </Link>

                    <h2 className="text-2xl font-semibold text-[#1F2937] mb-2 font-['Outfit']">
                        Forgot Password?
                    </h2>
                    <p className="text-[#4B5563] mb-6">
                        Enter your email address and we'll send you a link to reset your password.
                    </p>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleForgotPassword} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#4B5563]" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 input-field"
                                    required
                                    data-testid="forgot-email-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#064E3B] hover:bg-[#047857] text-white h-11"
                            data-testid="send-reset-link-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Send Reset Link'
                            )}
                        </Button>
                    </form>
                </div>
            </div>
        </BgWrapper>
    );
};

export default ForgotPassword;
